import {
  Avatar,
  AvatarUserType,
  ChakraBox as Box,
  ChakraFlex as Flex,
  DateFormat,
  FAIconName,
  FireflyButton,
  FireflyKebabMenu,
  FireflyKebabMenuItem,
  Tag,
  Text,
  convertDateFormat,
} from '@equip.health/ui';
import { ReactNode, useMemo } from 'react';

import { AssessmentDisplayStatus } from './AssessmentDisplayStatus';
import { AssessmentListCardItemRiskLevelTag } from './AssessmentListCardItemRiskLevelTag';
import { RiskLevel } from './RiskLevel';
import { StatusBadgeToColorMap } from './StatusBadgeToColorMap';

const idPrefix = 'list-item';
export interface AssessmentCardListItemProps {
  assignedTo?: string;
  createdDate: string;
  endDate?: string;
  assessmentResponseId: string;
  status: AssessmentDisplayStatus;
  submittedDate: string;
  scores?: (number | null)[];
  responseUpdatedBy: string | null;
  responseUpdatedByAvatarSrc?: string | null;
  responseUpdatedByUserType?: AvatarUserType;
  riskCategories?: (RiskLevel | null)[];
  responseUpdatedByUserId?: string | null;
  onDeleteClick: (assessmentResponseId: string) => void;
  onDownloadClick: (assessmentResponseId: string) => void;
  onResumeClick: (assessmentResponseId: string) => void;
  onViewAssessmentClick: (assessmentResponseId: string) => void;
  avatarTooltipWrapper?: (props: {
    children: React.ReactNode;
    providerExternalId: string;
  }) => ReactNode;
}
export const getStatusButtonLabel = (status: string): 'View' | 'Resume' => {
  switch (status) {
    case AssessmentDisplayStatus.INCOMPLETE:
    case AssessmentDisplayStatus.COMPLETE:
      return 'View';
    default:
      return 'Resume';
  }
};

const getStatusIconName = (status: AssessmentDisplayStatus) => {
  return [
    AssessmentDisplayStatus.COMPLETE,
    AssessmentDisplayStatus.INCOMPLETE,
  ].includes(status)
    ? FAIconName.externalLinkRegular
    : FAIconName.penLine;
};

const AssessmentCardListItem = ({
  assessmentResponseId,
  assignedTo,
  createdDate,
  endDate,
  submittedDate,
  status,
  scores,
  responseUpdatedBy,
  responseUpdatedByUserId,
  responseUpdatedByAvatarSrc,
  responseUpdatedByUserType = 'patient',
  riskCategories,
  onDeleteClick,
  onDownloadClick,
  onResumeClick,
  onViewAssessmentClick,
  avatarTooltipWrapper,
}: AssessmentCardListItemProps) => {
  const kebabOptions: FireflyKebabMenuItem[] =
    status === AssessmentDisplayStatus.IN_PROGRESS ||
    status === AssessmentDisplayStatus.NEW
      ? [
          {
            leftIcon: FAIconName.penLine,
            label: 'Resume',
            onClick: () => onResumeClick(assessmentResponseId),
          },
          {
            leftIcon: FAIconName.externalLinkRegular,
            label: 'View',
            onClick: () => onViewAssessmentClick(assessmentResponseId),
          },
          {
            leftIcon: FAIconName.trashRegular,
            leftIconColor: 'critical.base',
            label: 'Delete',
            onClick: () => onDeleteClick(assessmentResponseId),
          },
        ]
      : [
          {
            leftIcon: FAIconName.externalLinkRegular,
            label: 'View',
            onClick: () => onViewAssessmentClick(assessmentResponseId),
          },
          {
            leftIcon: FAIconName.arrowToBottomRegular,
            label: 'Download',
            onClick: () => onDownloadClick(assessmentResponseId),
          },
        ];
  const isStatusNew = status === AssessmentDisplayStatus.NEW;
  const isStatusInProgressOrIncomplete =
    status === AssessmentDisplayStatus.IN_PROGRESS ||
    status === AssessmentDisplayStatus.INCOMPLETE;

  const isStatusComplete = status === AssessmentDisplayStatus.COMPLETE;

  const dateColText: string = useMemo(() => {
    if (isStatusNew) {
      return createdDate
        ? `Created ${convertDateFormat(
            createdDate,
            DateFormat.longDateWithTime,
            DateFormat.shortDayMonthDate,
          )}`
        : '—';
    } else if (isStatusInProgressOrIncomplete) {
      return submittedDate
        ? `Updated ${convertDateFormat(
            submittedDate,
            DateFormat.longDateWithTime,
            DateFormat.shortDayMonthDate,
          )}`
        : '—';
    } else if (isStatusComplete) {
      return endDate
        ? `Completed ${convertDateFormat(
            endDate,
            DateFormat.longDateWithTime,
            DateFormat.shortDayMonthDate,
          )}`
        : '—';
    }
    return '—';
  }, [status, createdDate, submittedDate, endDate]);
  const respondentText: string = useMemo(() => {
    if (isStatusNew) {
      return assignedTo || '—';
    } else if (isStatusComplete || isStatusInProgressOrIncomplete) {
      return responseUpdatedBy || '—';
    }
    return '—';
  }, [status, assignedTo, responseUpdatedBy]);

  const isRespondentProvider =
    respondentText === responseUpdatedBy &&
    responseUpdatedByUserType === 'provider';
  return (
    <Flex
      alignItems="center"
      background="neutral.background.primary"
      borderBottom="1px"
      borderBottomColor="neutral.line"
      gap="18px"
      height="60px"
    >
      <Box flex="0 0 88px">
        <Tag
          color={StatusBadgeToColorMap[status as AssessmentDisplayStatus]}
          id={`${idPrefix}-status__${assessmentResponseId}`}
          value={status}
        />
      </Box>

      <Flex flex="0 0 72px">
        <FireflyButton
          id="assessment-row-action"
          leftIcon={getStatusIconName(status)}
          onClick={() => {
            if (getStatusButtonLabel(status) === 'Resume') {
              return onResumeClick(assessmentResponseId);
            }
            if (getStatusButtonLabel(status) === 'View') {
              return onViewAssessmentClick(assessmentResponseId);
            }
          }}
          size="small"
          variant="tertiary"
        >
          {getStatusButtonLabel(status)}
        </FireflyButton>
      </Flex>

      {((scores && scores.length > 0) ||
        (riskCategories && riskCategories.length > 0)) && (
        <Flex flex="1" justifyContent="space-evenly" minWidth="182px">
          <>
            {scores &&
              scores.length > 0 &&
              scores.map((score, index) => (
                <Flex
                  alignItems="center"
                  flex="0 0 67px"
                  justifyContent="center"
                  key={`score-risk-${index}`}
                >
                  <Text color="neutral.primary" variant="body">
                    {score === null ? '—' : score}
                  </Text>
                  {score != null &&
                    riskCategories &&
                    riskCategories.length > index &&
                    (riskCategories[index] !== null ? (
                      <Box paddingLeft="10px">
                        <AssessmentListCardItemRiskLevelTag
                          id={`${idPrefix}-risk-category__${assessmentResponseId}__${riskCategories[index]}`}
                          riskLevel={riskCategories[index] as RiskLevel}
                        />
                      </Box>
                    ) : (
                      <Text color="neutral.primary" variant="body">
                        —
                      </Text>
                    ))}
                </Flex>
              ))}
            {riskCategories &&
              riskCategories.length > 0 &&
              (!scores || scores.length === 0) &&
              riskCategories.map((riskCategory, index) => (
                <Flex
                  flex="0 0 67px"
                  justifyContent="center"
                  key={`risk-${index}`}
                >
                  {riskCategory !== null ? (
                    <AssessmentListCardItemRiskLevelTag
                      id={`${idPrefix}-risk-category__${assessmentResponseId}__${riskCategory}`}
                      riskLevel={riskCategory}
                    />
                  ) : (
                    <Text color="neutral.primary" variant="body">
                      —
                    </Text>
                  )}
                </Flex>
              ))}
          </>
        </Flex>
      )}

      <Box flex="1" minWidth="136px">
        <Text color="neutral.secondary" variant="caption" whiteSpace="nowrap">
          {dateColText}
        </Text>
      </Box>
      <Flex
        alignItems="center"
        flex="1"
        gap="18px"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {respondentText !== '—' && (
          <Avatar
            id={responseUpdatedByUserId || undefined}
            name={respondentText}
            size="xs"
            src={
              isRespondentProvider && responseUpdatedByAvatarSrc
                ? responseUpdatedByAvatarSrc
                : undefined
            }
            tooltipWrapper={
              isRespondentProvider ? avatarTooltipWrapper : undefined
            }
            userType={responseUpdatedByUserType || 'patient'}
          />
        )}
        <Text
          color="neutral.primary"
          overflow="hidden"
          textOverflow="ellipsis"
          variant="caption"
          whiteSpace="nowrap"
        >
          {respondentText}
        </Text>
      </Flex>
      <Box flex="0 0 20px">
        <FireflyKebabMenu aria-label="assessment menu" options={kebabOptions} />
      </Box>
    </Flex>
  );
};

export default AssessmentCardListItem;
